import { unpackRules } from "@casl/ability/extra";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk, RootState } from "../../app/store";
import { abilitiesCall, autoLoginCall } from "../../services/auth";
import { ProfileData, User } from "../../shared/models";
import http from "../../shared/utils/http";
import socketService from "../socket/socket.service";

// import socketService from "../socket/socket.service";

// AuthState interface definition
export interface AuthState {
	auth?: boolean;
	user?: User;
	profile?: Partial<ProfileData>;
	abilities: any[];
	socket?: string;
}

const initialState: AuthState = {
	profile: {},
	abilities: [],
	socket: undefined,
};

export const authSlice = createSlice({
	name: "auth",
	initialState,
	// The `reducers` field lets us define reducers and generate associated actions
	reducers: {
		setUser: (state, action: PayloadAction<any>) => {
			state.user = { ...state.user, ...action.payload };
		},
		resetUser: (state) => {
			state.user = undefined;
		},
		setAuth: (state, action: PayloadAction<boolean>) => {
			state.auth = action.payload;
		},
		setProfile: (state, action: PayloadAction<Partial<ProfileData>>) => {
			state.profile = { ...state.profile, ...action.payload };
		},
		resetProfile: (state) => {
			state.profile = undefined;
		},
		setAbilities: (state, action: PayloadAction<any[]>) => {
			state.abilities = action.payload;
		},
		resetAbilities: (state) => {
			state.abilities = [];
		},

		setSocketId: (state, action: PayloadAction<string | undefined>) => {
			state.socket = action.payload; // Store the socket correctly in the state
		},
		resetSocketId: (state) => {
			state.socket = undefined; // Reset the socket on logout or disconnection
		},
	},
});

// Export actions
export const {
	setUser,
	setAuth,
	resetUser,
	setProfile,
	resetProfile,
	setAbilities,
	resetAbilities,
	setSocketId,
	resetSocketId,
} = authSlice.actions;

export const loginAction =
	(callback: () => void): AppThunk =>
	(dispatch) => {
		dispatch(authAction(callback));
	};

export const authAction =
	(callback?: () => void, silent = false): AppThunk =>
	(dispatch) => {
		return autoLoginCall(silent)
			.then((res) => {
				if (res) {
					dispatch(
						setUser({
							userId: res.userId,
							actorId: res.actorId,
							profileCompleted: res.profileCompleted,
							accountType: res.accountType,
							accountLevel: res.accountLevel,
							username: res.username,
							name: res.name,
							surname: res.surname,
							sport: res.sport,
							avatarVersion: res.avatarVersion,
							profilePictureUrl: res.profilePictureUrl,
							unreadMessagesCount: res.unreadMessagesCount,
						}),
					);
				}
				const socket = socketService.connect(res.actorId, dispatch);

				dispatch(setSocketId(socket.id));

				return abilitiesCall().then((abilities) => {
					dispatch(setAbilities(unpackRules(abilities)));
					dispatch(setAuth(true));
					callback && callback();
				});
			})
			.catch((error) => {
				dispatch(setAuth(false));
				if (!silent) {
					throw error;
				}
			});
	};

// Other actions like login, logout etc.

export const authCompleteProfileAction =
	(): AppThunk => (dispatch, getState) => {
		const user = {
			...getState().auth.user,
			profileCompleted: true,
		};

		dispatch(setUser(user));
	};

export const logoutAction = (): AppThunk => async (dispatch, getState) => {
	const user = getState().auth.user;
	await http.post("/auth/logout", {});

	dispatch(setAuth(false));
	dispatch(resetUser());
	socketService.disconnect(user?.actorId || "");
	dispatch(resetSocketId());
};

export const selectUser = (state: RootState) => state.auth.user;
export const selectProfile = (state: RootState) => state.auth.profile;
export const selectAbilities = (state: RootState) => state.auth.abilities;
export const selectAuth = (state: RootState) => state.auth.auth;

export default authSlice.reducer;
